import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, PieChart, BarChart, Wallet, ArrowUpRight, ArrowDownRight, Target, Landmark, Receipt, PiggyBank, Plus, CreditCard, Filter, Search } from "lucide-react";
import { Area, AreaChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoalDialog } from "@/components/goals/GoalDialog";
import { BudgetDialog } from "@/components/budgets/BudgetDialog";
import { BudgetList } from "@/components/budgets/BudgetList";
import { SubscriptionCard } from "@/components/subscriptions/SubscriptionCard";
import { SubscriptionDialog } from "@/components/subscriptions/SubscriptionDialog";
import { SubscriptionStats } from "@/components/subscriptions/SubscriptionStats";
import { useToast } from "@/hooks/use-toast";
import { Subscription, CreateSubscription } from "@/types/subscription";

// Mock data for spending trends
const spendingTrends = [
  { month: 'Jan', amount: 2400 },
  { month: 'Feb', amount: 1900 },
  { month: 'Mar', amount: 3200 },
  { month: 'Apr', amount: 2800 },
  { month: 'May', amount: 2600 },
  { month: 'Jun', amount: 3100 },
];

// Mock data for recent transactions
const recentTransactions = [
  { id: 1, name: 'Grocery Store', amount: -82.55, type: 'expense', category: 'Food' },
  { id: 2, name: 'Salary Deposit', amount: 2500.00, type: 'income', category: 'Income' },
  { id: 3, name: 'Restaurant', amount: -45.80, type: 'expense', category: 'Food' },
  { id: 4, name: 'Online Shopping', amount: -129.99, type: 'expense', category: 'Shopping' },
];

// Mock account balances
const accounts = [
  { id: 1, name: 'Checking Account', balance: 4580.23, type: 'checking' },
  { id: 2, name: 'Savings Account', balance: 12750.45, type: 'savings' },
  { id: 3, name: 'Investment Account', balance: 8920.78, type: 'investment' },
];

// Updated mock savings goals with current amount
const initialSavingsGoals = [
  { id: 1, name: 'Emergency Fund', current: 5000, target: 10000 },
  { id: 2, name: 'Vacation', current: 2000, target: 3000 },
  { id: 3, name: 'New Car', current: 15000, target: 25000 },
];

// Mock subscription data
const initialSubscriptions: Subscription[] = [
  {
    id: 1,
    name: 'Netflix',
    amount: 15.99,
    frequency: 'monthly' as const,
    nextBilling: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days from now
    category: 'Entertainment',
    status: 'active' as const,
    color: '#E50914'
  },
  {
    id: 2,
    name: 'Spotify',
    amount: 9.99,
    frequency: 'monthly' as const,
    nextBilling: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(), // 12 days from now
    category: 'Music',
    status: 'active' as const,
    color: '#1DB954'
  },
  {
    id: 3,
    name: 'Adobe Creative Cloud',
    amount: 239.88,
    frequency: 'yearly' as const,
    nextBilling: new Date(Date.now() + 45 * 24 * 60 * 60 * 1000).toISOString(), // 45 days from now
    category: 'Productivity',
    status: 'active' as const,
    color: '#FF0000'
  },
  {
    id: 4,
    name: 'Gym Membership',
    amount: 29.99,
    frequency: 'monthly' as const,
    nextBilling: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days from now
    category: 'Fitness',
    status: 'active' as const,
    color: '#FF6B35'
  },
  {
    id: 5,
    name: 'Hulu',
    amount: 12.99,
    frequency: 'monthly' as const,
    nextBilling: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago (expired)
    category: 'Entertainment',
    status: 'cancelled' as const,
    color: '#1CE783'
  }
];

const Track = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [savingsGoals, setSavingsGoals] = useState(initialSavingsGoals);
  const [isGoalDialogOpen, setIsGoalDialogOpen] = useState(false);
  
  // Subscription management state
  const [subscriptions, setSubscriptions] = useState(initialSubscriptions);
  const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState<Subscription | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const { toast } = useToast();

  const handleAddGoal = (newGoal: { name: string; target: number }) => {
    setSavingsGoals([
      ...savingsGoals,
      {
        id: savingsGoals.length + 1,
        name: newGoal.name,
        current: 0,
        target: newGoal.target,
      },
    ]);
    console.log("New goal added:", newGoal);
  };

  // Filter subscriptions based on search and category
  const filteredSubscriptions = subscriptions.filter(sub => {
    const matchesSearch = sub.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || sub.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  // Get unique categories for filter
  const categories = ['all', ...Array.from(new Set(subscriptions.map(sub => sub.category)))];

  const handleAddSubscription = (newSubscription: CreateSubscription) => {
    const subscription = {
      ...newSubscription,
      id: Math.max(...subscriptions.map(s => s.id), 0) + 1
    };
    setSubscriptions([...subscriptions, subscription]);
    toast({
      title: "Subscription added",
      description: `${newSubscription.name} has been added to your subscriptions.`,
    });
  };

  const handleEditSubscription = (subscription: Subscription) => {
    setEditingSubscription(subscription);
    setIsSubscriptionDialogOpen(true);
  };

  const handleUpdateSubscription = (updatedSubscription: CreateSubscription) => {
    if (!editingSubscription) return;
    
    setSubscriptions(subs => 
      subs.map(sub => 
        sub.id === editingSubscription.id 
          ? { ...updatedSubscription, id: editingSubscription.id }
          : sub
      )
    );
    setEditingSubscription(undefined);
    toast({
      title: "Subscription updated",
      description: `${updatedSubscription.name} has been updated.`,
    });
  };

  const handleDeleteSubscription = (id: number) => {
    const subscription = subscriptions.find(sub => sub.id === id);
    setSubscriptions(subs => subs.filter(sub => sub.id !== id));
    toast({
      title: "Subscription deleted",
      description: `${subscription?.name} has been removed from your subscriptions.`,
      variant: "destructive",
    });
  };

  const handleSubscriptionDialogSave = (subscription: CreateSubscription) => {
    if (editingSubscription) {
      handleUpdateSubscription(subscription);
    } else {
      handleAddSubscription(subscription);
    }
  };

  const handleSubscriptionDialogClose = () => {
    setIsSubscriptionDialogOpen(false);
    setEditingSubscription(undefined);
  };

  return (
    <div className="space-y-8 animate-fade-in p-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
          Track Your Finances
        </h1>
        <p className="text-gray-600">Monitor your spending, account balances, and subscriptions</p>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-6 gap-4 bg-transparent h-auto">
          <TabsTrigger 
            value="overview"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Wallet className="w-4 h-4 mr-2" />
            Overview
          </TabsTrigger>
          <TabsTrigger 
            value="spending"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <BarChart className="w-4 h-4 mr-2" />
            Spending
          </TabsTrigger>
          <TabsTrigger 
            value="transactions"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Transactions
          </TabsTrigger>
          <TabsTrigger 
            value="budgets"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <Target className="w-4 h-4 mr-2" />
            Budgets
          </TabsTrigger>
          <TabsTrigger 
            value="goals"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <PiggyBank className="w-4 h-4 mr-2" />
            Goals
          </TabsTrigger>
          <TabsTrigger 
            value="subscriptions"
            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Subscriptions
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {accounts.map((account) => (
              <Card key={account.id} className="hover:shadow-md transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-500">{account.name}</p>
                      <p className="text-2xl font-bold">${account.balance.toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-secondary/10 rounded-xl">
                      <Landmark className="w-6 h-6 text-secondary" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Spending Analytics Tab */}
        <TabsContent value="spending" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Spending Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={spendingTrends}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="amount"
                      stroke="#1CB0F6"
                      fill="#1CB0F6"
                      fillOpacity={0.1}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Recent Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="flex items-center justify-between p-4 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-full ${
                        transaction.type === 'income' ? 'bg-green-100' : 'bg-red-100'
                      }`}>
                        {transaction.type === 'income' ? (
                          <ArrowUpRight className="w-4 h-4 text-green-600" />
                        ) : (
                          <ArrowDownRight className="w-4 h-4 text-red-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">{transaction.name}</p>
                        <p className="text-sm text-gray-500">{transaction.category}</p>
                      </div>
                    </div>
                    <p className={`font-medium ${
                      transaction.type === 'income' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {transaction.type === 'income' ? '+' : '-'}
                      ${Math.abs(transaction.amount).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Budgets Tab */}
        <TabsContent value="budgets" className="mt-6">
          <BudgetList />
        </TabsContent>

        {/* Goals Tab */}
        <TabsContent value="goals" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Savings Goals</CardTitle>
              <Button
                onClick={() => setIsGoalDialogOpen(true)}
                size="sm"
                className="flex items-center gap-1"
              >
                <Plus className="w-4 h-4" />
                New Goal
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {savingsGoals.map((goal) => (
                  <div key={goal.id} className="space-y-2">
                    <div className="flex justify-between">
                      <span className="font-medium">{goal.name}</span>
                      <span className="text-gray-600">
                        ${goal.current.toLocaleString()} / ${goal.target.toLocaleString()}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full">
                      <div
                        className="h-full bg-accent rounded-full transition-all"
                        style={{ width: `${(goal.current / goal.target) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="mt-6">
          <div className="space-y-6">
            {/* Statistics Overview */}
            <SubscriptionStats subscriptions={subscriptions} />

            <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                  <Input
                    placeholder="Search subscriptions..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-input bg-background rounded-md text-sm"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category === 'all' ? 'All Categories' : category}
                    </option>
                  ))}
                </select>
              </div>

              <Button
                onClick={() => setIsSubscriptionDialogOpen(true)}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Subscription
              </Button>
            </div>

            {/* Subscriptions Grid */}
            {filteredSubscriptions.length === 0 ? (
              <Card className="p-12 text-center">
                <div className="flex flex-col items-center gap-4">
                  <CreditCard className="w-12 h-12 text-muted-foreground" />
                  <div>
                    <h3 className="text-lg font-semibold">No subscriptions found</h3>
                    <p className="text-muted-foreground">
                      {searchTerm || filterCategory !== 'all' 
                        ? 'Try adjusting your search or filter criteria.' 
                        : 'Add your first subscription to get started tracking your recurring payments.'
                      }
                    </p>
                  </div>
                  {!searchTerm && filterCategory === 'all' && (
                    <Button
                      onClick={() => setIsSubscriptionDialogOpen(true)}
                      className="flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      Add Your First Subscription
                    </Button>
                  )}
                </div>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSubscriptions.map((subscription) => (
                  <SubscriptionCard
                    key={subscription.id}
                    subscription={subscription}
                    onEdit={handleEditSubscription}
                    onDelete={handleDeleteSubscription}
                  />
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <GoalDialog
        open={isGoalDialogOpen}
        onOpenChange={setIsGoalDialogOpen}
        onSave={handleAddGoal}
      />

      <SubscriptionDialog
        open={isSubscriptionDialogOpen}
        onOpenChange={handleSubscriptionDialogClose}
        onSave={handleSubscriptionDialogSave}
        subscription={editingSubscription}
      />
    </div>
  );
};

export default Track;
